import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { ArrowDown, ArrowUp, History, Loader2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  SortableTableHead,
  sortItems,
  useTableSort,
} from "~/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useStockMovements } from "~/hooks/purchasing";
import type {
  IngredientStockMovement,
  IngredientType,
  OilType,
  Plant,
  RawMaterial,
} from "~/types/purchasing";
import { MOVEMENT_TYPE_COLORS, MOVEMENT_TYPE_LABELS } from "~/types/purchasing";
import { litersToKg } from "~/utils/oilConversions";

const getMovementSortValue = (
  movement: IngredientStockMovement,
  field: string
): string | number | null => {
  switch (field) {
    case "date":
      return dayjs(movement.created_at).valueOf();
    case "plant":
      return movement.plant;
    case "type":
      return MOVEMENT_TYPE_LABELS[movement.movement_type];
    case "reference":
      return movement.reference_number ?? null;
    case "quantity":
      return movement.quantity;
    case "cost":
      return movement.unit_cost ?? null;
    case "balance":
      return movement.balance_after;
    default:
      return null;
  }
};

interface StockMovementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredientType: IngredientType;
  ingredient: OilType | RawMaterial;
}

export function StockMovementsDialog({
  open,
  onOpenChange,
  ingredientType,
  ingredient,
}: StockMovementsDialogProps) {
  const { data: movements, isLoading } = useStockMovements(
    ingredientType,
    ingredient.id
  );
  const [plantFilter, setPlantFilter] = useState<Plant | "all">("all");
  const { sort, toggleSort } = useTableSort();

  const filteredMovements = useMemo(() => {
    if (!movements) return movements;
    if (plantFilter === "all") return movements;
    return movements.filter((m) => m.plant === plantFilter);
  }, [movements, plantFilter]);

  const sortedMovements = useMemo(() => {
    if (!filteredMovements) return filteredMovements;
    return sortItems(filteredMovements, sort, getMovementSortValue);
  }, [filteredMovements, sort]);

  // Oils are stored in liters (source of truth) but bought/counted in kg, so we
  // display them in kg (liters × density) instead of the misleading "liters / 1000
  // = Ton". Raw materials keep their native unit.
  const oilDensity =
    ingredientType === "oil"
      ? (ingredient as OilType).density_kg_per_liter
      : null;

  const formatKg = (kg: number) =>
    `${kg.toLocaleString("es-CL", { maximumFractionDigits: 2 })} kg`;

  const formatQuantity = (quantity: number, unit: string) => {
    const sign = quantity >= 0 ? "+" : "";
    if (oilDensity && oilDensity > 0) {
      return `${sign}${formatKg(litersToKg(quantity, oilDensity))}`;
    }
    if (unit === "liters") {
      return `${sign}${quantity.toFixed(2)} Lt`;
    }
    return `${sign}${quantity.toFixed(2)} ${unit}`;
  };

  const formatBalance = (balance: number, unit: string) => {
    if (oilDensity && oilDensity > 0) {
      return formatKg(litersToKg(balance, oilDensity));
    }
    if (unit === "liters") {
      return `${balance.toFixed(2)} Lt`;
    }
    return `${balance.toFixed(2)} ${unit}`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return "-";
    return `$${amount.toLocaleString("es-CL")}`;
  };

  const ingredientName =
    ingredientType === "oil"
      ? (ingredient as OilType).name
      : (ingredient as RawMaterial).name;

  const ingredientCode =
    ingredientType === "oil"
      ? (ingredient as OilType).code
      : (ingredient as RawMaterial).code;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historial de Movimientos
          </DialogTitle>
          <DialogDescription>
            Movimientos de stock para{" "}
            <span className="font-medium text-foreground">
              {ingredientCode} - {ingredientName}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b pb-3">
          <span className="text-sm text-muted-foreground">Planta:</span>
          <Select
            value={plantFilter}
            onValueChange={(v) => setPlantFilter(v as Plant | "all")}
          >
            <SelectTrigger className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="P1">P1</SelectItem>
              <SelectItem value="P2">P2</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !filteredMovements || filteredMovements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <History className="mb-4 h-12 w-12 opacity-50" />
              <p className="text-lg font-medium">Sin movimientos</p>
              <p className="text-sm">
                Aún no hay movimientos registrados para este ingrediente
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    sortKey="date"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[120px]"
                  >
                    Fecha
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="plant"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[80px]"
                  >
                    Planta
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="type"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[100px]"
                  >
                    Tipo
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="reference"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    Referencia
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="quantity"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[120px] text-right"
                  >
                    Cantidad
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="cost"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[100px] text-right"
                  >
                    Costo Unit.
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="balance"
                    sort={sort}
                    onSort={toggleSort}
                    className="w-[120px] text-right"
                  >
                    Saldo
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMovements?.map((movement, index) => (
                  <TableRow
                    key={movement.id}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  >
                    <TableCell className="font-medium">
                      {dayjs(movement.created_at).format("DD/MM/YYYY")}
                      <div className="text-xs text-muted-foreground">
                        {dayjs(movement.created_at).format("HH:mm")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{movement.plant}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={MOVEMENT_TYPE_COLORS[movement.movement_type]}
                      >
                        {MOVEMENT_TYPE_LABELS[movement.movement_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {movement.reference_number || "-"}
                        </span>
                        {movement.notes && (
                          <span className="text-xs text-muted-foreground">
                            {movement.notes}
                          </span>
                        )}
                        {movement.performed_by_profile && (
                          <span className="text-xs text-muted-foreground">
                            Por: {movement.performed_by_profile.name}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div
                        className={`flex items-center justify-end gap-1 font-medium ${
                          movement.quantity >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {movement.quantity >= 0 ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )}
                        {formatQuantity(movement.quantity, movement.unit)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(movement.unit_cost)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatBalance(movement.balance_after, movement.unit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
